import { Link } from 'react-router-dom'

import { useClient } from '../App'
import type { Project } from '../lib/api'
import { fmtDate, humanize, statusClass } from '../lib/format'

const HIDDEN_PROFILE_KEYS = new Set(['id', 'type'])

function ProfileCard(): JSX.Element {
  const { me } = useClient()
  const entries = Object.entries(me.profile).filter(
    ([k, v]) => !HIDDEN_PROFILE_KEYS.has(k) && v !== null && v !== '',
  )
  return (
    <section className="card">
      <h2>Your Details</h2>
      {entries.length === 0 ? (
        <p className="muted">No contact details are shared to your portal yet.</p>
      ) : (
        <dl className="detail-grid">
          {entries.map(([k, v]) => (
            <div key={k} className="detail-pair">
              <dt>{humanize(k)}</dt>
              <dd>{String(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  )
}

function ProjectCard({ project }: { project: Project }): JSX.Element {
  const addr = [
    project.address_street,
    [project.address_city, project.address_state].filter(Boolean).join(', '),
    project.address_zip,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <Link to={`/projects/${project.id}/schedule`} className="card project-card">
      <div className="project-card-head">
        <h3>{project.display_name}</h3>
        <span className={`badge badge-${statusClass(project.status)}`}>
          {humanize(project.status)}
        </span>
      </div>
      {addr && <p className="muted">{addr}</p>}
      <dl className="detail-grid compact">
        <div className="detail-pair">
          <dt>Phase</dt>
          <dd>{project.current_phase ? humanize(project.current_phase) : '—'}</dd>
        </div>
        <div className="detail-pair">
          <dt>Target Completion</dt>
          <dd>{fmtDate(project.target_completion_date)}</dd>
        </div>
      </dl>
      <span className="card-cta">View project →</span>
    </Link>
  )
}

export function OverviewPage(): JSX.Element {
  const { me, projects } = useClient()
  return (
    <div className="page">
      <h1>Welcome, {me.display_name}</h1>
      <ProfileCard />
      <section>
        <h2>Your Projects</h2>
        {projects.length === 0 ? (
          <div className="card empty-card">
            <p>No projects are linked to your portal yet.</p>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
